import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Snowflake } from "lucide-react";
import { api, fmtNum, fmtDate, COMPANY, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-dashed border-slate-200 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`font-semibold text-slate-900 text-right ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

export default function Nota() {
  const { id } = useParams();
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.get(`/transactions/${id}`).then((r) => setTx(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);

  if (err) return <div className="min-h-screen aqua-bg flex items-center justify-center text-rose-600" data-testid="nota-error">{err}</div>;
  if (!tx) return <div className="min-h-screen aqua-bg flex items-center justify-center text-slate-500">Memuat nota...</div>;
  const isIn = tx.type === "in";

  return (
    <div className="min-h-screen aqua-bg py-8 px-4" data-testid="nota-page">
      <div className="max-w-[560px] mx-auto mb-4 flex items-center justify-between no-print">
        <Link to="/transactions" className="inline-flex items-center gap-1 text-sm text-sky-700 font-semibold hover:underline" data-testid="link-back-transactions"><ArrowLeft className="h-4 w-4" /> Kembali</Link>
        <Button onClick={() => window.print()} className="rounded-full bg-sky-600 hover:bg-sky-700" data-testid="btn-print-nota"><Printer className="h-4 w-4 mr-1" /> Cetak / Simpan PDF</Button>
      </div>

      <div className="nota-sheet max-w-[560px] mx-auto bg-white rounded-2xl border border-sky-100 shadow-xl p-8 text-slate-900" data-testid="nota-sheet">
        <div className="flex items-start justify-between gap-4 pb-5 border-b-2 border-slate-900">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 flex items-center justify-center" data-testid="nota-logo"><img src={COMPANY.logo} alt="Logo" className="h-full w-full object-contain" /></div>
            <div>
              <div className="font-extrabold tracking-tight text-lg uppercase leading-tight" data-testid="nota-company">{COMPANY.name}</div>
              <div className="text-xs text-slate-500">{COMPANY.tagline} • {COMPANY.address}</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isIn ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="nota-type">
              Nota Stok {isIn ? "Masuk" : "Keluar"}
            </div>
            <div className="mt-2 font-mono text-sm font-bold" data-testid="nota-number">{tx.receipt_no}</div>
            <div className="text-xs text-slate-500">{fmtDate(tx.date)}</div>
          </div>
        </div>

        <div className="mt-5">
          <Row label="Kode Batch" value={tx.batch_code} mono />
          <Row label="Jenis Ikan" value={tx.species_name} />
          <Row label="Size / Ukuran" value={tx.size} />
          <Row label="Asal Ikan" value={tx.origin_name} />
          <Row label="Lokasi Ruang" value={tx.location} />
          {!isIn && <Row label="Tujuan / Pembeli" value={tx.destination || "-"} />}
          {isIn && tx.entry_date && <Row label="Tanggal Masuk Batch" value={fmtDate(tx.entry_date)} />}
        </div>

        <div className="mt-5 rounded-xl bg-sky-50 border border-sky-100 p-4 grid grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Jumlah</div>
            <div className="text-3xl font-extrabold font-mono tracking-tight" data-testid="nota-quantity">{fmtNum(tx.quantity_mc)} <span className="text-base text-sky-700">{tx.unit || "MC"}</span></div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Bobot Estimasi</div>
            <div className="text-3xl font-extrabold font-mono tracking-tight" data-testid="nota-weight">{fmtNum(tx.total_weight_kg)} <span className="text-base text-sky-700">kg</span></div>
            {(tx.unit || "MC") === "MC" && <div className="text-[11px] text-slate-500">@ {fmtNum(tx.weight_per_mc_kg)} kg / MC</div>}
          </div>
        </div>

        {tx.note && <div className="mt-4 text-sm"><span className="text-slate-500">Catatan: </span>{tx.note}</div>}

        {!isIn && (
          <div className="mt-4 rounded-xl border border-slate-200 p-3 text-xs" data-testid="nota-bank-info">
            <div className="font-bold uppercase tracking-wider text-slate-500 mb-1.5">Pembayaran ke rekening</div>
            {COMPANY.banks.map((b) => <div key={b.bank} className="flex justify-between"><span className="text-slate-600">Bank {b.bank}</span><span className="font-mono font-semibold">{b.account_no} <span className="font-sans font-normal text-slate-500">a.n. {b.holder}</span></span></div>)}
          </div>
        )}

        <div className="mt-8 grid grid-cols-3 gap-4 text-center text-xs">
          {[["Diserahkan oleh", tx.user_name], [isIn ? "Sopir / Pengirim" : "Sopir / Pengangkut", ""], [isIn ? "Diterima Gudang" : "Diterima Pembeli", ""]].map(([t, n]) => (
            <div key={t}>
              <div className="h-16 border-b border-slate-400" />
              <div className="mt-1.5 font-semibold text-slate-700">{t}</div>
              <div className="text-slate-500 h-4">{n}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><Snowflake className="h-3 w-3" /> Disimpan pada suhu -22°C</span>
          <span>Operator: {tx.user_name} • Dicetak {new Date().toLocaleDateString("id-ID")}</span>
        </div>
      </div>
    </div>
  );
}
